<template>
  <div class="infinite-carousel" :class="{ disabled }">
    <div class="infinite-head" :class="{ container: title }">
      <slot name="heading" class="heading" />
      <component
        :is="heading" v-show="title" class="h4"
        :class="headingClass" v-html="title"
      />
      <slot v-if="exchanged" name="mobile-description" class="mobile description" />

      <div v-show="!disabled" class="controls">
        <CaretLeftCircle @click="prev" />
        <CaretRightCircle @click="next" />
      </div>
    </div>
    <slot v-if="!exchanged" name="description" class="description" />
    <div class="carousel">
      <div
        ref="inner" v-swipe.right="prev" v-swipe.left="next"
        class="inner" :style="!disabled ? innerStyles : {}"
      >
        <slot name="slide" />
      </div>
    </div>
  </div>
</template>

<script>
import events from '@itbpbg/lms-components/src/mixins/events';
import CaretLeftCircle from '../assets/svg/caret-left-circle.svg?vue-component';
import CaretRightCircle from '../assets/svg/caret-right-circle.svg?vue-component';

export default {
  components: {
    CaretLeftCircle,
    CaretRightCircle,
  },
  mixins: [events],
  props: {
    autoRun: { type: Number, default: 0, required: false },
    exchanged: { type: Boolean, required: false },
    align: { type: String, default: 'center', required: false },
    title: { type: String, default: '', required: false },
    heading: { type: String, default: 'p', required: false },
    headingClass: { type: String, default: '', required: false },
    offset: { type: Number, default: 2, required: false },
    calculationsParentSelector: { type: String, default: '', required: false },
    disabled: Boolean,
  },
  data() {
    return {
      innerStyles: {},
      step: '',
      transitioning: false,
      center: 0,
      screenWidth: 0,
      autoRunTimer: null,
      carouselHovered: false,
    };
  },
  computed: {
    slides() {
      return this.$slots.slide;
    },
  },
  watch: {
    // eslint-disable-next-line func-names
    '$store.state.layout': function () {
      this.$nextTick(() => this.setStep(true));
    },
  },
  mounted() {
    window.addEventListener('resize', () => this.setStep());
    this.$nextTick(() => this.setStep(true));

    if (this.autoRun) {
      const carousel = this.$el.querySelector('.carousel');
      this.addEventListeners([
        [carousel, 'mouseenter', () => {
          this.carouselHovered = true;
          clearTimeout(this.autoRunTimer);
        }],
        [carousel, 'mouseleave', () => {
          this.carouselHovered = false;
          this.autoRunCarousel();
        }],
      ]);
      setTimeout(() => {
        this.autoRunCarousel();
      }, 500);

      this.$once('hook:beforeDestroy', () => clearTimeout(this.autoRunTimer));
    }
  },
  unmounted() {
    clearTimeout(this.autoRunTimer);
    window.removeEventListener('resize', this.setStep);
  },
  methods: {
    autoRunCarousel() {
      if (this.autoRun && !this.carouselHovered) {
        this.autoRunTimer = setTimeout(() => {
          this.next();
        }, this.autoRun * 1000);
      }
    },
    setStep(force = false) {
      if (this.screenWidth !== window.innerWidth || force) {
        this.screenWidth = window.innerWidth;
        this.$nextTick(() => {
          const offset = this.offset || 0;
          const wrapper = this.$el.querySelector('.inner');
          const lastSlide = wrapper.lastChild;
          wrapper.insertBefore(lastSlide, wrapper.firstChild);
          const slide = this.slides[0].elm;
          const style = slide.currentStyle || window.getComputedStyle(slide);
          const slideWidth = slide.offsetWidth;
          let parentWidth = document.body.clientWidth;
          if (this.calculationsParentSelector) {
            parentWidth = document.querySelector(this.calculationsParentSelector).clientWidth;
          }
          const halfScreen = parentWidth / 2;
          const slideOffset = +style.marginRight.replace('px', '');
          const elementFullWidth = slideWidth + slideOffset;
          const elementsFittedInHalfScreen = Math.ceil(halfScreen / elementFullWidth);
          const exactWidthOfElementsToCenter = elementsFittedInHalfScreen * elementFullWidth + (slideWidth / 2);

          this.step = slideWidth + slideOffset;

          // centered alignment (default)
          this.center = exactWidthOfElementsToCenter - halfScreen;

          // container alignment
          if (this.align === 'container') {
            this.center = (((parentWidth - document.querySelector('main .container').offsetWidth) / 2) - (this.step * (offset + 1))) * -1;
          }

          // left alignment
          if (this.align === 'left') {
            this.center = 0;

            if (offset) {
              this.center = (this.step * offset);
            }
          }

          this.resetTranslate();
        });
      }
    },
    next() {
      if (!this.$refs.inner) return;
      if (this.transitioning || (this.autoRun && !this.autoRunTimer)) return;
      this.transitioning = true;

      clearTimeout(this.autoRunTimer);
      this.moveLeft();

      this.$emit('slide-changed');

      this.afterTransition(() => {
        this.resetTranslate();

        if (this.transitioning) {
          const wrapper = this.$el.querySelector('.inner');
          const firstSlide = wrapper.firstChild;
          firstSlide.remove();
          wrapper.appendChild(firstSlide);
        }
        this.transitioning = false;

        if (this.autoRun) {
          this.autoRunCarousel();
        }
      });
    },
    prev() {
      if (!this.$refs.inner) return;
      if (this.transitioning) return;
      this.transitioning = true;
      clearTimeout(this.autoRunTimer);

      const wrapper = this.$el.querySelector('.inner');
      const lastSlide = wrapper.lastChild;
      const clone = lastSlide.cloneNode(true);
      wrapper.insertBefore(clone, wrapper.firstChild);
      clone.style.position = 'absolute';
      clone.style.right = '100%';
      const leftDefault = clone.style.left;
      clone.style.left = 'auto';

      this.moveRight();

      this.$emit('slide-changed');

      this.afterTransition(() => {
        this.resetTranslate();

        if (this.transitioning) {
          lastSlide.remove();
          clone.style.left = leftDefault;
          clone.style.removeProperty('right');
          clone.style.removeProperty('position');
        }
        this.transitioning = false;

        if (this.autoRun) {
          this.autoRunCarousel();
        }
      });
    },
    moveLeft() {
      const value = +this.step;

      this.innerStyles = {
        transform: `translateX(-${this.center + value}px)`,
      };
    },
    moveRight() {
      const value = +this.step;

      this.innerStyles = {
        transform: `translateX(${(this.center - value) * -1}px)`,
      };
    },
    afterTransition(callback) {
      const listener = (e) => {
        // callback only when ending transition is transform and is on transitioned div
        if (e.propertyName === 'transform' && e.target.classList.contains('inner')) {
          callback();
          this.$refs.inner.removeEventListener('transitionend', listener);
        }
      };
      this.$refs.inner.addEventListener('transitionend', listener);
    },
    resetTranslate() {
      if (this.center < 0) {
        this.center *= -1;
      }

      this.innerStyles = {
        transition: 'none',
        transform: `translateX(-${this.center}px)`,
      };
    },
  },
};
</script>

<style lang="scss">
.infinite-carousel {
  .description {
    &.mobile {
      display: none;
    }
  }

  .controls {
    text-align: center;
    margin: var(--spacing-24) 0 var(--spacing-36);

    svg {
      cursor: pointer;

      circle[fill] {
        fill: transparent;
      }

      circle[stroke] {
        stroke: var(--main-light);
      }

      path[stroke] {
        stroke: var(--main-light);
      }

      &:first-of-type {
        margin-right: var(--spacing-14);
      }
    }
  }

  .carousel {
    margin: 0 auto;
    width: 100%;
    overflow: hidden;

    .inner {
      transition: transform 1s;
      white-space: nowrap;
      text-align: center;

      > * {
        margin-right: 10px;
        border-radius: 4px;
        align-items: center;
        justify-content: center;
        display: inline-flex;
        overflow: hidden;
        white-space: normal;
        text-align: left;

        > * {
          flex: auto;
        }
      }
    }

    /* optional */
    button {
      margin-right: 5px;
      margin-top: 10px;
    }
  }
}
</style>
